import { DateTime } from 'luxon';
import { generateColorFromId, formatLabel, sanitizeModeName } from '../../utils/modeScheduleUtils';
import { showComment } from '../../utils/modeScheduleUtils';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
import { bpSlotsToBlocks } from '../../utils/modeScheduleUtils';

function formatHour(hourFloat) {
    const hour = Math.floor(hourFloat);
    const minute = Math.round((hourFloat - hour) * 60);
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

function BatteryStatus({ device }) {
    const measurements = device?.measurements || {};
    const power = measurements?.power || 0;
    const status = device?.connected || false;
    const deleted = device?.deleted || false;

    const sensors = measurements?.sensors || {};
    const soc = sensors?.soc || 0;

    let measurementTime = measurements?.time;
    if (measurementTime) {
        measurementTime = DateTime.fromISO(measurementTime).toFormat('yyyy-MM-dd HH:mm:ss');
    }

    return (
        <div className="summary__card summary__card--battery">
            <h3 className="summary__title">
                <i className="fa fa-battery-three-quarters" style={{ marginRight: '6px' }}></i>
                Battery Status
            </h3>
            <div className="summary__progress">
                <div className="summary__progress-bar">
                    <div className="summary__progress-fill" style={{ width: `${soc * 100}%` }}></div>
                </div>
                <span className="summary__progress-label">{(soc * 100).toFixed(2)}%</span>
            </div>
            <div className="summary__battery-info">
                <div className="summary__battery-item">
                    <span className="summary__battery-label">Status</span>
                    {deleted ? (
                        <span className="summary__battery-value summary__battery-value--red">Deleted</span>
                    ) : status ? (
                        <span className="summary__battery-value summary__battery-value--green">Connected</span>
                    ) : (
                        <span className="summary__battery-value summary__battery-value--black">Disconnected</span>
                    )}
                </div>
                <div className="summary__battery-item">
                    <span className="summary__battery-label">Power</span>
                    <span className="summary__battery-value">{(Math.round(power * 100) / 100).toFixed(2)} kW</span>
                </div>
                <div className="summary__battery-item">
                    <span className="summary__battery-label">Last measurement</span>
                    <span className="summary__battery-value">{measurementTime}</span>
                </div>
            </div>
        </div>
    );
}

function ModesLegend({ modes }) {
    // Sort modes to ensure 'fcr' modes come last
    modes.sort((a, b) => {
        if (a.name.startsWith('fcr') && !b.name.startsWith('fcr')) {
            return 1; // 'fcr' modes come last
        } else if (!a.name.startsWith('fcr') && b.name.startsWith('fcr')) {
            return -1; // non-'fcr' modes come first
        }
        return a.name.localeCompare(b.name); // sort alphabetically otherwise
    });
    return (
        <div className="summary__card summary__card--modes">
            <h3 className="summary__title">
                {' '}
                <i className="fa fa-cogs" style={{ marginRight: '6px' }}></i>
                Operation Modes
            </h3>
            <ul className="summary__legend">
                {modes.length > 0 ? (
                    modes.map(mode => (
                        <li key={mode.id} className="summary__legend-item">
                            <span
                                className="legend-dot"
                                style={{
                                    backgroundColor: generateColorFromId(sanitizeModeName(mode.name)),
                                }}
                            ></span>
                            <div style={{ marginBottom: '-5px' }}>{formatLabel(mode.name)}</div>
                            <InfoTooltip className="summary__legend-item--tooltip" tooltip={showComment(mode.name)} />
                        </li>
                    ))
                ) : (
                    <li className="summary__legend-item">No modes available</li>
                )}
            </ul>
        </div>
    );
}

const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function ActiveMode({ modes, deviceSchedule }) {
    if (!deviceSchedule) {
        return (
            <div className="summary__card summary__card--active">
                <h3 className="summary__title">
                    {' '}
                    <i className="fa fa-check-circle" style={{ color: 'green', marginRight: '5px' }}></i>
                    Currently Active Mode
                </h3>
                <div className="summary__active-mode">
                    <span className="summary__mode-label">
                        <span className="legend-dot" style={{ backgroundColor: 'gray' }}></span>
                        <div style={{ marginBottom: '-2px' }}>no mode found</div>
                    </span>
                </div>
            </div>
        );
    }

    let daily_blocks = null;
    let weekly_blocks = null;
    if (deviceSchedule?.daily_bp) {
        daily_blocks = bpSlotsToBlocks(deviceSchedule.daily_bp.slots);
    } else if (deviceSchedule?.weekly_bp) {
        console.log('Weekly BP found:', deviceSchedule.weekly_bp);
        weekly_blocks = weekdays.reduce((acc, day) => {
            acc[day] = bpSlotsToBlocks(deviceSchedule.weekly_bp[day].slots);
            return acc;
        }, {});
    }

    const nowDateTime = DateTime.utc();
    const now = nowDateTime.hour + nowDateTime.minute / 60;

    const modeMap = modes.reduce((acc, mode) => {
        acc[mode.id] = mode.name;
        return acc;
    }, {});

    const todayKey = nowDateTime.setZone('utc').toFormat('cccc').toLowerCase();
    const todaysBlocks = daily_blocks ? daily_blocks : weekly_blocks ? weekly_blocks[todayKey] : [];

    todaysBlocks.sort((a, b) => a.start - b.start);

    let activeBlock = todaysBlocks.find(block => now >= block.start && now < block.end);

    if (!activeBlock) {
        const nextBlock = todaysBlocks.find(block => block.start > now);
        activeBlock = {
            mode: 0,
            start: now,
            end: nextBlock ? nextBlock.start : 24,
        };
    }
    const currentMode = modeMap[activeBlock.mode] || 'idle';
    return (
        <div className="summary__card summary__card--active">
            <h3 className="summary__title">
                {' '}
                <i className="fa fa-check-circle" style={{ color: 'green', marginRight: '5px' }}></i>
                Currently Active Mode
            </h3>
            <div className="summary__active-mode">
                <span className="summary__mode-label">
                    <span
                        className="legend-dot"
                        style={{ backgroundColor: activeBlock ? generateColorFromId(sanitizeModeName(currentMode)) : 'gray' }}
                    ></span>
                    <div style={{ marginBottom: '-2px' }}>{activeBlock ? formatLabel(currentMode) : 'idle'}</div>
                </span>
                <span className="summary__mode-time">
                    {formatHour(activeBlock.start)} - {formatHour(activeBlock.end)}
                </span>
                <span className="summary__mode-check">✔</span>
            </div>
        </div>
    );
}

export { ModesLegend, BatteryStatus, ActiveMode };
